<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import PaywallService from "@/services/PaywallService";
import { useStore } from 'vuex'
import { useNotification } from "@kyvg/vue3-notification";
import FormSelect from '../Atoms/FormSelect.vue';

const store = useStore()
const { notify } = useNotification()

const props = defineProps({
  currentPaywall: {
    type: [Object, Function],
  }
})

const maxSessions = ref('')
const saving = ref(false)

const syncFromPaywall = () => {
  const v = props.currentPaywall?.max_sessions
  maxSessions.value = (v === null || v === undefined || v === '') ? '' : String(v)
}

onMounted(syncFromPaywall)
watch(() => props.currentPaywall?.id, syncFromPaywall)

// Fixed choices keep this simple; a pre-existing custom value above 5 is kept
// as an option so it isn't silently changed.
const sessionOptions = computed(() => {
  const opts = [
    { value: '', text: 'Default (2 devices)' },
    { value: '0', text: 'Unlimited' },
    { value: '1', text: '1 device' },
    { value: '2', text: '2 devices' },
    { value: '3', text: '3 devices' },
    { value: '4', text: '4 devices' },
    { value: '5', text: '5 devices' },
  ]
  const cur = String(maxSessions.value ?? '')
  if (cur && !opts.some(o => o.value === cur)) {
    opts.push({ value: cur, text: `${cur} devices` })
  }
  return opts
})

const save = async () => {
  const raw = String(maxSessions.value ?? '').trim()
  const value = raw === '' ? null : parseInt(raw, 10)

  saving.value = true
  try {
    await PaywallService.updateStreamPaywallById(props.currentPaywall.id, { max_sessions: value === null ? '' : value })
    store.dispatch('Paywalls/setPaywallProp', { id: props.currentPaywall.id, prop: 'max_sessions', value })
    notify({ group: "success", text: "Concurrent viewing limit updated" })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't update the viewing limit" })
    syncFromPaywall()
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
    <h4 class="text-sm font-semibold text-white mb-0.5">Concurrent viewing limit</h4>
    <p class="text-tiny text-surface-7 mb-3">How many devices can watch at the same time on a single viewer account.</p>
    <div class="max-w-xs">
      <FormSelect v-model="maxSessions" :options="sessionOptions" @change="save" />
    </div>
  </section>
</template>

<style scoped></style>
